import type MarkdownIt from 'markdown-it';
import type StateCore from 'markdown-it/lib/rules_core/state_core.mjs';
import type Token from 'markdown-it/lib/token.mjs';

import { ACTIONABLE_FOLLOW_UPS_STATUS_PREFIX } from '../../../actionable-follow-ups/actionable-follow-ups';

const FOLLOW_UPS_REGEX = /\[\^(actionable-follow-ups)\^\]/;

/**
 * Replaces the `[^actionable-follow-ups^]` placeholder (injected when the `◈`
 * marker is extracted from the assistant response) with a `status` node whose
 * `localId`/`text` is prefixed with `ACTIONABLE_FOLLOW_UPS_STATUS_PREFIX`. The
 * renderer swaps that status node for the actionable follow-up questions UI,
 * pulling the parsed questions from the message's `markdownData`.
 *
 * Mirrors `md-plugin-inline-lumina`.
 */
const inlineFollowUps = (state: StateCore) => {
	const tokens: Token[] = [];
	let followUpsCounter = 0;

	for (const token of state.tokens) {
		const tokenChildren: Array<Token | null> = [];

		if (token.type !== 'inline' || (token.children && token?.children?.length <= 0)) {
			tokens.push(token);
			continue;
		}

		if (token.children) {
			token.children.forEach((childToken) => {
				if (childToken.type === 'text' && FOLLOW_UPS_REGEX.test(childToken.content)) {
					const parts = childToken.content.split(FOLLOW_UPS_REGEX);
					parts.forEach((part, index) => {
						if (index % 2 === 0) {
							const textToken = new state.Token('text', '', 0);
							textToken.content = part;
							textToken.level = childToken.level;
							tokenChildren.push(textToken);
						} else {
							const followUpsToken = new state.Token('status', '', 0);
							followUpsToken.content = '';
							followUpsToken.block = false;
							followUpsToken.level = childToken.level;
							followUpsToken.attrs = [
								['localId', ACTIONABLE_FOLLOW_UPS_STATUS_PREFIX + followUpsCounter],
								['text', ACTIONABLE_FOLLOW_UPS_STATUS_PREFIX + followUpsCounter],
								['color', 'blue'],
							];
							tokenChildren.push(followUpsToken);
							followUpsCounter++;
						}
					});

					return;
				}

				tokenChildren.push(childToken);
			});
		}

		const filteredTokenChildren = tokenChildren.filter(
			(childToken) => childToken !== null,
		) as Token[];
		token.children = filteredTokenChildren;
		tokens.push(token);
	}

	state.tokens = tokens;
	return;
};

export default function mdPluginInlineFollowUps(md: MarkdownIt): void {
	md.core.ruler.push('inline-follow-ups', inlineFollowUps);
}
